/**
 * AVExport — the visualizer as a video of a whole song, rendered offline.
 *
 * Live recording (MediaRecorder on the canvas) records in real time, drops
 * frames when the GPU is busy and stretches under load; 62 % of the live
 * recordings of 2026-09 were shorter than 3 s — people with a song wanted a
 * VIDEO of the song. This renders every frame at a fixed rate with the same
 * analysis (AudioReactiveCore) and the same renderer (AVRenderer) the page
 * shows, and encodes it with the song's audio through CanvasVideoExport
 * (WebCodecs; MP4 H.264+AAC or WebM VP9+Opus). Picture and sound are in sync
 * by construction — frame i shows the analysis of the audio at i / fps.
 *
 *   AVExport.run({
 *       file, ctx,                         // the picked file, the page AudioContext
 *       scene, postFx, sensitivity, motion,
 *       width, height, fps, format,        // format 'mp4' | 'webm'
 *       signal, onStage(name), onProgress(frac)
 *   }) → Promise<Blob>
 *   AVExport.supported({width, height, fps, format}) → Promise<{ok, lane?, reason?}>
 *
 * Two lanes, chosen by run() itself: 'direct' — picture and sound through
 * WebCodecs; 'remux' — the picture through WebCodecs, then the song's OWN
 * sound glued on by FFmpeg (CanvasVideoExport.muxAudioInto, `-c:v copy`):
 * Firefox has no AAC encoder (MP4), iOS 16.4–18 has no AudioEncoder at all.
 * run() rejects with code 'unsupported' when neither lane exists here, and
 * with code 'too_long' when the remux lane cannot hold the file in memory.
 *
 * Resolves null when the shared length gate refused the file (its dialog has
 * already spoken: longer than this device holds whole, or a phone advisory
 * the person declined). The song is decoded whole — decodeGated probes the
 * length first, so a recording that cannot fit is refused before the decode.
 *
 * Dependencies are loaded on first use (versioned through __jsSrc):
 * canvas-video-export.js (which loads its muxers and AacAccessUnit itself),
 * audio-decoder.js + webcodecs-audio-decode.js (AudioDecoderRobust: native,
 * then WebCodecs for the mp4 family, then FFmpeg).
 *
 * ES5 + Promises. window.AVExport.
 */
(function (root) {
    'use strict';

    var SLUG = 'audio-visualizer';
    var LEAD_S = 0.015;                /* analysis frame time → centre of its content (engine.js) */
    /* the longest render one page session is asked to hold open (a podcast
       episode); memory and the phone advisory usually bind first */
    var MAX_SEC = 60 * 60;
    /* peak working set in whole-file buffers: the decoded song + its mono mix */
    var MEM_MULTIPLIER = 2;
    var CRF = 20;

    function jsSrc(p) { return typeof root.__jsSrc === 'function' ? root.__jsSrc(p) : p; }

    var loading = {};
    function load(path, ready) {
        if (ready()) return Promise.resolve();
        if (loading[path]) return loading[path];
        loading[path] = new Promise(function (resolve, reject) {
            var s = document.createElement('script');
            s.src = jsSrc(path);
            s.async = true;
            var nonce = document.querySelector('script[nonce]');
            if (nonce && nonce.nonce) s.nonce = nonce.nonce;
            s.onload = function () { ready() ? resolve() : reject(new Error('loaded but not ready: ' + path)); };
            s.onerror = function () { loading[path] = null; reject(new Error('failed to load ' + path)); };
            document.head.appendChild(s);
        });
        return loading[path];
    }

    function ensureDeps() {
        return Promise.all([
            load('/js/canvas-video-export.js', function () { return !!root.CanvasVideoExport; }),
            load('/js/webcodecs-audio-decode.js', function () { return !!root.WebCodecsAudioDecode; }).catch(function () {}),
            load('/js/audio-decoder.js', function () { return !!root.AudioDecoderRobust; })
        ]);
    }

    function supported(o) {
        if (typeof root.VideoEncoder !== 'function') return Promise.resolve({ ok: false, reason: 'no-webcodecs' });
        return ensureDeps().then(function () { return root.CanvasVideoExport.isSupported(o); }).then(function (s) {
            var CVE = root.CanvasVideoExport;
            if (s && s.ok) return { ok: true, lane: 'direct', proven: s.proven };
            /* the probe tries the picture first: this reason means it works */
            if (s && typeof CVE.canRemuxAudioFor === 'function' && CVE.canRemuxAudioFor(s.reason)) {
                return { ok: true, lane: 'remux', reason: s.reason, proven: s.proven };
            }
            return { ok: false, reason: (s && s.reason) || 'unsupported' };
        });
    }

    function fail(code, message) { var e = new Error(message || code); e.code = code; return e; }

    function monoOf(buf) {
        var n = buf.length, ch = buf.numberOfChannels, out = new Float32Array(n);
        for (var c = 0; c < ch; c++) {
            var d = buf.getChannelData(c);
            for (var i = 0; i < n; i++) out[i] += d[i];
        }
        if (ch > 1) for (var j = 0; j < n; j++) out[j] /= ch;
        return out;
    }

    function waitReady(r, id) {
        return new Promise(function (resolve, reject) {
            var t0 = Date.now();
            (function poll() {
                if (r.isReady(id)) return resolve();
                if (Date.now() - t0 > 20000) return reject(new Error('shader compile timeout'));
                setTimeout(poll, 30);
            })();
        });
    }

    function run(o) {
        var stage = function (n) { try { if (o.onStage) o.onStage(n); } catch (e) {} };
        var Core = root.AudioReactiveCore;
        if (!Core || !root.AVRenderer || !root.AVScenes) return Promise.reject(new Error('visualizer modules missing'));
        var W = o.width, H = o.height, fps = o.fps || 30, format = o.format || 'mp4', lane = 'direct';
        var CVE;
        stage('decode');
        return supported({ width: W, height: H, fps: fps, format: format }).then(function (sup) {
            if (!sup.ok) throw fail('unsupported', sup.reason);
            lane = sup.lane;
            CVE = root.CanvasVideoExport;
            return root.AudioDecoderRobust.decodeGated(o.file, {
                slug: SLUG, ctx: o.ctx, signal: o.signal, maxS: MAX_SEC, multiplier: MEM_MULTIPLIER
            });
        }).then(function (res) {
            if (res && res.refused) return null;
            var buf = res && res.buffer;
            if (!buf || !buf.length) throw new Error('empty audio');
            if (o.signal && o.signal.aborted) throw new DOMException('aborted', 'AbortError');
            /* the remux holds the picture three times in FFmpeg memory: a song
               whose picture would not fit is refused before the render */
            if (lane === 'remux' && CVE.estimateBytes(W, H, fps, CRF, buf.duration, false) > CVE.remuxMaxBytes) {
                throw fail('too_long', 'remux-too-big');
            }
            stage('render');
            var sr = buf.sampleRate, x = monoOf(buf);
            var an = new Core.Analyzer(sr);
            var tr = new Core.Tracker(sr, { analyzer: an, background: 'silence' });
            tr.setSensitivity(o.sensitivity || 1);
            var canvas = document.createElement('canvas');
            canvas.width = W; canvas.height = H;
            var r = root.AVRenderer.create(canvas, { size: { w: W, h: H }, scenes: root.AVScenes });
            if (!r.ok) throw new Error('webgl');
            r.setScene(o.scene, true);
            r.setPostFx(o.postFx !== false);
            var n = an.fftSize, hop = an.hop, frame = new Float64Array(Core.FRAME_LEN), pad = new Float32Array(n);
            var end = hop, events = [];
            /* every analysis frame whose content is at or before t */
            function advance(t) {
                while (end <= x.length && end / sr <= t + LEAD_S) {
                    var win;
                    if (end >= n) win = x.subarray(end - n, end);
                    else { pad.fill(0); pad.set(x.subarray(0, end), n - end); win = pad; }
                    an.analyze(win);
                    an.writeFrame(end / sr, frame);
                    tr.step(frame);
                    for (var g = 0; g < 3; g++) if (tr.onsetEvent[g] > 0) events.push({ t: tr.onsetEventT[g], g: g, s: tr.onsetEvent[g] });
                    if (tr.beatEvent) events.push({ t: end / sr, g: 3, s: 1 });
                    end += hop;
                }
            }
            var done = function (v) { try { r.destroy(); } catch (e) {} return v; };
            return waitReady(r, o.scene).then(function () {
                return CVE.encode({
                    width: W, height: H, fps: fps, durationSec: buf.duration,
                    format: format,
                    audioBuffer: lane === 'direct' ? buf : null,
                    qualityCrf: CRF,
                    signal: o.signal,
                    onProgress: o.onProgress,
                    drawFrame: function (ctx2d, i, t) {
                        advance(t);
                        var ev = events; events = [];
                        r.render({ time: t, dt: i ? 1 / fps : 0, d: tr.d, events: ev, now: t, motion: o.motion == null ? 1 : o.motion });
                        ctx2d.drawImage(canvas, 0, 0, W, H);
                    }
                });
            }).then(done, function (err) { done(); throw err; });
        }).then(function (blob) {
            if (!blob || lane !== 'remux') return blob;
            stage('sound');
            return CVE.muxAudioInto(blob, o.file, { format: format, signal: o.signal }).then(function (out) {
                if (o.signal && o.signal.aborted) throw new DOMException('aborted', 'AbortError');
                if (!out) throw fail('remux_failed');
                return out;
            });
        });
    }

    root.AVExport = { run: run, supported: supported, ensureDeps: ensureDeps, MAX_SEC: MAX_SEC };
})(typeof window !== 'undefined' ? window : this);
